import { AuthData } from "../auth";
import {
  RestFeeds,
  RestFeedUpdate,
  RestUserFeed,
  RestFolderUpdate,
  PostMarker,
  RestStarList,
  RestUnreadFeed,
  FeedFilter,
} from "../../schema-copy/api-schema";
import {
  LocalFeedMarker,
  UnreadChangeCallback,
  FeedLimits,
  MAX_TRACKED_POSTS,
  ReadMarkerEngine,
} from "./read-marker-engine";
import { calculateAnonInitialUnread, computePauseEndBoundary } from "../../schema-copy/readmarker-helper";

/** A feed's recent post times (newest first), as of postCount, see RestUserFeed.recentPostsSemi */
export interface FeedSemisSnapshot {
  semis: number[];
  postCount: number;
  fetchedAt: number;
}

export type { LocalFeedMarker, UnreadChangeCallback, FeedLimits };

/**
 * Copy a feed's current.dz location (RestFeedUpdate.cdn / syncInstance) from a server response onto
 * the locally held record. Every path that refreshes a feed in place must call this: the location is
 * not fixed at subscribe time. A feed created by an add is stored with no location at all — it gets
 * one on its first store, moments later — and the post engine needs `syncInstance` to read
 * current.dz from the origin. Without it, a feed whose current.dz was never uploaded (every feed
 * below the CDN subscriber threshold, i.e. every freshly added one) 404s against the CDN until the
 * next full load.
 *
 * A response that omits a field (an older server) leaves the value we have alone. `cdn: null` is a
 * real value — "on no CDN" — so the undefined check there has to be strict.
 */
export function applyCurrentFileLocation(feed: RestUserFeed, update: Pick<RestFeedUpdate, 'cdn' | 'syncInstance' | 'siteCss'>): void {
  if (update.cdn) feed.cdn = update.cdn;
  if (update.syncInstance) feed.syncInstance = update.syncInstance;
  // Rides along for the same reason: the per-site stylesheet hash changes with the stylesheet, on
  // the feed's next fetch. `null` is a real value ("the site has no stylesheet now").
  if (update.siteCss !== undefined) feed.siteCss = update.siteCss;
}

// Bulk-subscribe flows (e.g. following a whole collection) add many feeds at once, so they cap a fresh
// feed's unread tighter than a manual single subscribe: keep at least 10, at most 20, and nothing
// older than 48h.
export const ONBOARDING_MAX_UNREAD = 20;
export const ONBOARDING_MAX_AGE_HOURS = 48;

// Offline classification:
// - 'event': browser fired an 'offline' event. Engine is paused.
// - 'poll':  a feed-engine request (poll or getFeed) got no response.
//            Engine keeps retrying.
export type OfflineMode = 'none' | 'event' | 'poll';
export type OfflineChangeCallback = (mode: OfflineMode) => void;

// Engine states
export type EngineState =
  | 'OFF'         // No user logged in
  | 'LOADING'     // Loading initial data
  | 'ERROR'       // Initial load failed
  | 'READY'       // Data available, following update schedule
  | 'PAUSED'      // Data available, but not updating (window inactive etc)
  | 'UPDATING'    // Data available, actively requesting update
  | 'INTERRUPTED'; // Data available, but last update failed

export type StateChangeCallback = (state: EngineState, prevState: EngineState) => void;
export type FeedsChangeCallback = (feeds: RestUserFeed[], folders: RestFolderUpdate) => void;
export type StarChangeCallback = (starredCount: number, readLaterCount: number) => void;
export type NoticeChangeCallback = (notice: LocalNotice | null) => void;

/** Local notice with pre-computed shutdown time */
export interface LocalNotice {
  message: string;
  /** Absolute local timestamp (ms) when the server will shut down, if a shutdown is planned */
  shutdownAtMs?: number;
}

/**
 * The feed engine's public contract — the surface the UI and other connectors depend on.
 *
 * Two implementations exist: `AuthFeedEngine` (logged-in; talks to the authenticated API) and
 * `AnonFeedEngine` (no-login; subscriptions/markers/preferences/starred/read-later live in
 * localStorage and feed data comes from the public `/api/anon/*` endpoints + CDN). Both `extends`
 * this class, so the TypeScript compiler verifies they implement the whole surface — keeping the
 * two engines interchangeable for every consumer (`appState.feedEngine: FeedEngine`).
 *
 * `enable`'s `auth` is optional because the anon engine ignores it; the auth engine requires it
 * in practice (App always passes it).
 */
/** Display data for a feed's reader header, see {@link FeedEngine.rememberFeedHeader}. */
export interface FeedHeaderHint {
  title: string;
  iconUrl?: string;
  circularIcon?: boolean;
}

export abstract class FeedEngine {
  // Title and icon of feeds the user is about to open from a tile (Home Discover, Add Feed), so the
  // reader header shows them right away while an unfollowed feed's info is still being fetched.
  // In memory only, and only consulted when getFeed() doesn't know the feed.
  private readonly feedHeaderHints = new Map<string, FeedHeaderHint>();

  rememberFeedHeader(feedId: string, hint: FeedHeaderHint): void {
    this.feedHeaderHints.set(feedId, hint);
  }

  getFeedHeaderHint(feedId: string): FeedHeaderHint | undefined {
    return this.feedHeaderHints.get(feedId);
  }

  // Lifecycle
  abstract enable(auth?: AuthData): Promise<void>;
  abstract disable(): void;
  abstract destroy(): void;
  abstract pause(): void;
  abstract resume(): Promise<void>;
  abstract reloadData(): Promise<void>;
  /** Trigger an immediate poll if the engine is idle (READY); no-op otherwise.
   * Used by the UI to force a server marker reconciliation when it detects the
   * local marker state can't be right (e.g. a feed whose bitmap says all read
   * but whose unread count is > 0 — the drift is fixed by the next poll's
   * parseServerMarkers). */
  abstract pollNow(): Promise<void>;
  abstract getState(): EngineState;
  abstract getData(): RestFeeds | null;
  /** Date (YYYYMMDD) of the current home/page-{date}.json, when the server told us (RestFeeds.homeDate). */
  abstract getHomeDate(): string | undefined;

  // Event subscriptions (return an unsubscribe function)
  abstract onStateChange(callback: StateChangeCallback): () => void;
  abstract onUnreadChange(callback: UnreadChangeCallback): () => void;
  abstract onFeedsChange(callback: FeedsChangeCallback): () => void;
  abstract onStarChange(callback: StarChangeCallback): () => void;
  abstract onNoticeChange(callback: NoticeChangeCallback): () => void;
  abstract onOfflineChange(callback: OfflineChangeCallback): () => void;
  abstract onUnreadFeedChange(callback: () => void): () => void;

  // Offline + notices
  abstract getOfflineMode(): OfflineMode;
  /** Turn the offline-reading snapshot on or off (the user's `offlineReading` preference). When on,
   * the engine mirrors everything it downloads into the user's JsonStore; turning it off deletes it. */
  abstract setOfflineReading(enabled: boolean): void;
  /** True when the app can be used without the server (offline reading on, library loaded). */
  abstract canReadOffline(): boolean;
  /** Delete the posts and images stored for offline reading. They stay when offline reading is
   *  switched off, so switching it on again reuses them. */
  abstract clearOfflineCache(): Promise<void>;
  abstract isOffline(): boolean;
  abstract getNotice(): LocalNotice | null;

  // Per-feed display settings
  abstract getSortOrder(feedId: string): 'N' | 'O' | 'A' | '?';
  abstract setSortOrder(feedId: string, order: 'N' | 'O' | 'A'): void;
  abstract getShowRead(feedId: string): 'u' | 'a';
  abstract setShowRead(feedId: string, showRead: 'u' | 'a'): void;
  abstract getCompactMode(feedId: string): 'c' | 'f' | 'l';
  abstract setCompactMode(feedId: string, mode: 'c' | 'f' | 'l'): void;

  // Auto-mark-read limits
  abstract getFeedLimits(feedId: string): FeedLimits;
  abstract setFeedLimits(feedId: string, maxUnread: number, maxAgeDays: number): void;
  abstract ensureFeedSemis(feedId: string): Promise<void>;
  abstract previewAutoRead(feedId: string, maxUnread: number, maxAgeDays: number): number | undefined;

  // Pausing. A paused feed counts as all read: posts are marked read as they arrive (by the engine's
  // auto-read pass, see enforcePause). The pause itself is stored in the feed's read marker.
  protected abstract markerEngine: ReadMarkerEngine;
  protected feedSemis = new Map<string, FeedSemisSnapshot>();

  /** Semi the feed's pause ends (PAUSED_INDEFINITELY = no end); undefined when not paused. */
  getPausedUntil(feedId: string): number | undefined {
    return this.isFeedPaused(feedId) ? this.markerEngine.getPausedUntil(feedId) : undefined;
  }

  isFeedPaused(feedId: string): boolean {
    return this.markerEngine.isPaused(feedId);
  }

  /** Pauses the feed until the given semi (PAUSED_INDEFINITELY = no end), or extends its pause,
   *  and marks all its posts read. */
  pauseFeed(feedId: string, pausedUntil: number): void {
    this.markerEngine.setPausedUntil(feedId, pausedUntil);
    this.markAllRead(feedId);
  }

  /** Ends the feed's pause. Everything stays read except the newest post, so the feed has
   *  something to show and doesn't look dead. */
  unpauseFeed(feedId: string): void {
    this.markerEngine.setPausedUntil(feedId, 0);
    this.markAllRead(feedId);
    const postCount = this.getFeed(feedId)?.postCount;
    if (postCount) this.markPostUnread(feedId, postCount - 1);
  }

  /**
   * Keeps a paused feed read: marks the posts that arrived since the last pass. Once the pause has
   * ended, marks the posts that arrived before its end (the engine may not have been running then),
   * using the feed's post times, and removes the pause. Returns true while the feed is paused.
   * `fetched` is set on the retry after loading post times, so a failed load can't loop.
   */
  protected enforcePause(feedId: string, fetched = false): boolean {
    const until = this.markerEngine.getPausedUntil(feedId);
    if (!until) return false;
    const postCount = this.getFeed(feedId)?.postCount ?? 0;
    const start = Math.max(0, postCount - MAX_TRACKED_POSTS);
    const hasUnread = !!postCount && !!this.markerEngine.countUnreadAtOrBelow(feedId, postCount - 1);
    if (this.markerEngine.isPaused(feedId)) {
      if (hasUnread) this.markerEngine.markPostsRead(feedId, [[start, postCount - 1]]);
      return true;
    }

    if (hasUnread) {
      // Post times from before the pause end can't tell about the posts that came after them.
      const snap = this.feedSemis.get(feedId);
      const boundary = snap && snap.fetchedAt >= until * 10000
        ? computePauseEndBoundary(snap.semis, snap.postCount, until) : undefined;
      if (boundary == undefined && !fetched) {
        this.feedSemis.delete(feedId);
        this.ensureFeedSemis(feedId).then(() => this.enforcePause(feedId, true));
        return false;
      }
      // Unknown even with fresh post times (the pause ended long ago): leave the posts unread.
      if (boundary != undefined && boundary >= start) {
        console.log(`[pause] feed ${feedId}: pause ended, marking posts ${start}-${boundary} read`);
        this.markerEngine.markPostsRead(feedId, [[start, boundary]]);
      }
    }
    this.markerEngine.setPausedUntil(feedId, 0);
    return false;
  }

  // Keyword filters (pro/vip only; auth engine only — the anon engine stubs these out).
  // setFeedFilter applies the filter engine-side (incl. the retroactive pass); persisting
  // the filter into UserPreferences.feedFilters is the caller's job.
  abstract getFeedFilter(feedId: string): FeedFilter | undefined;
  abstract setFeedFilter(feedId: string, filter: FeedFilter | undefined): Promise<void>;
  abstract previewFeedFilter(feedId: string, filter: FeedFilter | undefined): Promise<number>;

  // Read markers
  abstract getFeedMarker(feedId: string): LocalFeedMarker | undefined;
  abstract getAllFeedMarkers(): Map<string, LocalFeedMarker>;
  abstract isPostRead(feedId: string, postIndex: number): boolean;
  abstract getReadForFeed(feedId: string): Set<number>;
  abstract getUnreadCount(feedId: string): number;
  abstract markPostRead(feedId: string, postIndex: number): void;
  abstract markPostUnread(feedId: string, postIndex: number): void;
  abstract markPostsRead(feedId: string, postIndices: PostMarker[]): void;
  abstract markAllRead(feedId: string): void;
  abstract markAllUnread(feedId: string): void;
  abstract preloadFeed(feedId: string, count?: number): void;
  abstract markOlderThanDaysRead(feedId: string, days: number): Promise<void>;
  abstract markOlderThanKeepRead(feedId: string, days: number, keep: number): Promise<void>;

  // Feeds
  abstract getFeeds(): RestUserFeed[];
  abstract getFeed(feedId: string): RestUserFeed | undefined;
  abstract getRawFeed(feedId: string): RestUserFeed | undefined;
  abstract registerFeedInfo(feed: RestUserFeed): void;
  abstract ensureFeedInfo(feedId: string): Promise<RestUserFeed | undefined>;

  // Subscriptions
  abstract subscribe(feedId: string): Promise<RestUserFeed | null>;
  abstract unsubscribe(feedId: string): Promise<boolean>;

  /**
   * Cap a freshly subscribed feed's unread to the standard starter limit: keep the newest ~10 unread,
   * plus everything from the last 72h, never more than 100 — mark the rest read. Used by the
   * bulk-subscribe flows so a batch of new feeds doesn't dump hundreds of
   * unread posts. Idempotent: re-marking already-read posts is a no-op. Each engine supplies the
   * feed's recent post times; the shared math lives in {@link applyStarterLimitWith}.
   */
  abstract applyStarterReadLimit(feedId: string): Promise<void>;

  protected applyStarterLimitWith(feedId: string, recentPostsSemi: number[] | undefined, postCount: number,
      maxUnread: number = 100, maxAgeHours: number = 72): void {
    if (!postCount) return;
    const keepUnread = calculateAnonInitialUnread(recentPostsSemi, postCount, Date.now(), maxUnread, maxAgeHours);
    const readBoundary = postCount - keepUnread - 1; // highest index to mark read
    if (readBoundary < 0) return;
    const start = Math.max(0, postCount - MAX_TRACKED_POSTS);
    if (start > readBoundary) return;
    this.markPostsRead(feedId, [[start, readBoundary]]);
  }
  abstract handleSubscribeResult(result: { feed: RestUserFeed; feedMarker: string; bitmap: string; refreshing?: boolean }): void;

  /**
   * Finish a subscription the server reports as already existing (an 'AlreadySubscribed' error):
   * make the local state reflect it and return the feed, or undefined if it can't be resolved.
   * Must not touch read markers — the existing subscription already has them.
   */
  abstract adoptExistingSubscription(feedId: string): Promise<RestUserFeed | undefined>;
  abstract isRecentlySubscribed(feedId: string): boolean;
  /** True while the server is (probably still) fetching the feed after a subscribe — drives the
   *  "refreshing" spinner. Auth engine: server-confirmed fetch, anon engine: recent-subscribe heuristic. */
  abstract isRefreshing(feedId: string): boolean;
  abstract getPrivateFeedCount(): number;
  abstract getNewsletterFeedCount(): number;

  // Folders
  abstract getFolders(): RestFolderUpdate;
  abstract setFeedFolder(feedId: string, folderName: string | null): Promise<boolean>;
  abstract moveFeed(feedId: string, folderName: string | null, position?: number): Promise<boolean>;
  abstract moveFeeds(feedIds: string[], folderName: string | null, position?: number): Promise<boolean>;
  abstract addEmptyFolder(folderName: string): Promise<boolean>;
  abstract deleteFolder(folderName: string, deleteFeeds: boolean): Promise<boolean>;
  abstract renameFolder(oldName: string, newName: string): Promise<boolean>;
  abstract setFolderOrder(orderedNames: string[]): Promise<boolean>;
  abstract mergeFolders(source: string, target: string): Promise<boolean>;

  // Starred / read-later
  abstract isStarred(feedId: string, postIndex: number): boolean;
  abstract isReadLater(feedId: string, postIndex: number): boolean;
  abstract getStarredForFeed(feedId: string): Set<number>;
  abstract getReadLaterForFeed(feedId: string): Set<number>;
  abstract getStarredEntries(): string[];
  abstract getReadLaterEntries(): string[];
  abstract getStarredCount(): number;
  abstract getReadLaterCount(): number;
  /** Returns false when the save was refused (anonymous saved-posts cap reached). */
  abstract starPost(feedId: string, postIndex: number): boolean;
  abstract unstarPost(feedId: string, postIndex: number): void;
  /** Returns false when the save was refused (anonymous saved-posts cap reached). */
  abstract markReadLater(feedId: string, postIndex: number): boolean;
  abstract unmarkReadLater(feedId: string, postIndex: number): void;
  abstract fetchStarredList(count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null>;
  abstract fetchReadLaterList(count?: number, sortOrder?: 'newestFirst' | 'oldestFirst'): Promise<RestStarList | null>;

  // Cross-feed unread list (the /a/new view); with folder, limited to that folder's feeds (/a/new/<folder>)
  abstract fetchUnreadFeed(sortOrder: 'newestFirst' | 'oldestFirst', count?: number, folder?: string): Promise<RestUnreadFeed | null>;
  abstract getUnreadFeedEntries(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): string[] | null;
  abstract getUnreadFeedInfo(sortOrder: 'newestFirst' | 'oldestFirst', folder?: string): { hasMore: boolean; totalLoaded: number } | null;
  abstract clearUnreadFeedCache(sortOrder?: 'newestFirst' | 'oldestFirst', folder?: string): void;
}
